import { useQuery } from '@tanstack/react-query';
import { CrewMerchantApi, CrewMerchantUi } from 'corso-types';
import api from '~/api';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import { useIsCorsoCxAgent } from '~/hooks/useIsCorsoAdmin';
import { useIsConciergeWidget } from '~/hooks/widgetHooks';
import { useStoreId } from '~/providers/StoreUserProvider';

type SearchKind = CrewMerchantApi.RequestBody<
  '/:storeId/search',
  'post'
>['kind'];

type ConciergeSearchKind = CrewMerchantApi.RequestBody<
  '/concierge/search',
  'post'
>['kind'];

const isConciergeKind = (kind: SearchKind): kind is ConciergeSearchKind =>
  kind === 'Order' || kind === 'Shipping';

/** Corso CX agents working inside the concierge widget search every store, not just the active one. */
export function useIsConciergeSearch() {
  const isConciergeWidget = useIsConciergeWidget();
  const isCorsoCxAgent = useIsCorsoCxAgent();

  return isConciergeWidget && isCorsoCxAgent;
}

// one or two characters match too broadly to be a useful result, and fan out across every enabled kind
export const MIN_SEARCH_TERM_LENGTH = 3;

export function useSearchByKind(
  searchTerm: string,
  kind: SearchKind,
  { storeOnly = false }: { storeOnly?: boolean } = {},
) {
  const storeId = useStoreId();
  const {
    isReturnsEnabled,
    isWarrantyEnabled,
    isShippingProtectionEnabled,
    isRegistrationEnabled,
  } = useEnabledClaimType();

  const isEmbeddedCx = useIsConciergeWidget();
  const isConciergeSearch = useIsConciergeSearch() && !storeOnly;

  // the active store's settings say nothing about the store a platform result belongs to
  const conciergeKindEnabled = {
    Return: false,
    Warranty: false,
    Shipping: true,
    Registration: false,
    Order: true,
    Product: false,
  } as const satisfies Record<SearchKind, boolean>;

  const storeKindEnabled = {
    Return: isReturnsEnabled && !isEmbeddedCx,
    Warranty: isWarrantyEnabled && !isEmbeddedCx,
    Shipping: isShippingProtectionEnabled,
    Registration: isRegistrationEnabled && !isEmbeddedCx,
    Order: true,
    Product: true,
  } as const satisfies Record<SearchKind, boolean>;

  const kindEnabled = isConciergeSearch
    ? conciergeKindEnabled
    : storeKindEnabled;

  return useQuery({
    queryKey: [
      'globalSearch',
      kind,
      isConciergeSearch ? 'platform' : storeId,
      searchTerm,
    ],
    enabled:
      searchTerm.length >= MIN_SEARCH_TERM_LENGTH &&
      searchTerm.length <= CrewMerchantUi.MAX_SEARCH_TERM_LENGTH &&
      kindEnabled[kind],
    queryFn: () => {
      const body = {
        cursor: null,
        orderBy: 'desc',
        take: 5,
        filters: { searchTerm },
        // ? quickSearch always leverages openSearch
        shouldUseOpenSearch: true,
      } as const;

      return isConciergeSearch && isConciergeKind(kind)
        ? api.conciergeSearch({ ...body, kind })
        : api.store(storeId).search({ ...body, kind });
    },
  });
}
